import type { LineSeriesOption } from "echarts/charts";
import type { GridComponentOption, TitleComponentOption, TooltipComponentOption } from "echarts/components";
import type { ComposeOption } from "echarts/core";
import { AxisSide, type AxisSpec, type TimeAxisPanelSpec } from "../ChartSpec";
import { formatAxisPointerTime, formatTooltipValue, seriesNamesDiffer } from "../format";
import type { GetDisplayInfo } from "../units/use-get-display-info";
import { type AxisScale, formatTickLabel } from "./layout/axis-layout";
import {
    GRID_BORDER_COLOR,
    X_AXIS_POINTER_LABEL_MARGIN_PX,
    X_AXIS_TICK_LABEL_FONT_SIZE_PX,
    X_AXIS_TICK_LABEL_LINE_HEIGHT_PX,
    X_AXIS_TICK_LABEL_MARGIN_PX,
    Y_AXIS_BASE_OFFSET_PX,
    Y_TICK_FONT_SIZE_PX,
} from "./layout/plot-geometry";
import type { PlotLayout } from "./layout/plot-layout";
import type { PreparedSeries } from "./prepare-series";
import { renderTooltipHtml } from "./tooltip/tooltip-html";

type ECOption = ComposeOption<LineSeriesOption | TitleComponentOption | TooltipComponentOption | GridComponentOption>;

/**
 * Y axes are driven entirely by pre-computed scales (see layout/axis-layout.ts): we set numeric
 * min/max/interval so ECharts renders exactly the ticks we already measured for gutter width.
 * Ticks above `maxVisibleTick` sit in the unit-badge headroom band and render as empty strings —
 * a pure closure over an already-computed number, not state mutated by an ECharts callback.
 */
const buildYAxes = (
    axes: AxisSpec[],
    axisScales: Map<string, AxisScale>,
    activeLeftAxisId: string | undefined,
): ECOption["yAxis"] => {
    // All axes share one offset, so only one per side can show tick labels without overlapping.
    // Extra axes stay in the list (series still bind to them) but hide labels. Keep in sync with
    // visibleAxesForSide in plot-layout.ts.
    const firstRightAxisId = axes.find((axis) => axis.side === AxisSide.Right)?.id;

    return axes.map((axis) => {
        const isVisible = axis.side === AxisSide.Left ? axis.id === activeLeftAxisId : axis.id === firstRightAxisId;
        const scale = axisScales.get(axis.id);

        return {
            id: axis.id,
            type: "value",
            position: axis.side,
            offset: Y_AXIS_BASE_OFFSET_PX,
            // No scale entry means this axis has no data. Omit min/max/interval and let ECharts
            // auto-range: nothing is plotted, so exact ticks don't matter.
            ...(scale ? { min: scale.niceMin, max: scale.niceMax, interval: scale.step } : {}),
            axisLabel: {
                show: isVisible,
                color: "#838879",
                // fontWeight/fontSize must stay in sync with Y_TICK_FONT in ./layout/plot-geometry.ts —
                // the gutter width was measured with that font string.
                fontWeight: "lighter",
                fontSize: Y_TICK_FONT_SIZE_PX,
                hideOverlap: true,
                margin: 0,
                formatter: (value: number) => {
                    if (!scale) return formatTooltipValue(value);
                    return value <= scale.maxVisibleTick ? formatTickLabel(value, scale.step) : "";
                },
            },
            splitLine: {
                show: false,
            },
            axisPointer: {
                // Same visibility as tick labels: inactive left axes still drive series scales, but
                // their crosshair badges would stack on top of the active metric's value.
                label: {
                    show: isVisible,
                    formatter: (params) =>
                        typeof params.value === "number" ? formatTooltipValue(params.value) : String(params.value),
                },
            },
        };
    });
};

export const buildTimePanelOptions = (
    spec: TimeAxisPanelSpec,
    series: PreparedSeries[],
    layout: PlotLayout,
    axisScales: Map<string, AxisScale>,
    window: { startMs: number; endMs: number },
    activeLeftAxisId: string | undefined,
    getDisplayInfo: GetDisplayInfo,
): ECOption => {
    const showDeviceName = seriesNamesDiffer(series);
    const seriesByStreamId = new Map(series.map((s) => [s.streamId, s]));

    return {
        // Grant corrections (a peer panel widening the shared gutter) and axis-toggle reflows must
        // land as an instant snap, not a tween — an animated grid change is what turns an already-
        // brief cross-panel realignment into a visible slide. See use-panel-plot-layout.ts.
        animation: false,
        grid: {
            // Border handled by PlotChromeLayer, a DOM overlay in panel.tsx.
            show: false,
            // We set exact pixel gutters from the shared width-group grant; containLabel would
            // re-fit the grid around whatever labels ECharts measures and fight those numbers.
            containLabel: false,
            top: 0,
            left: layout.gutters.left,
            right: layout.gutters.right,
            bottom: layout.xAxisBandPx,
        },
        tooltip: {
            trigger: "axis",
            textStyle: { lineHeight: 14 },
            formatter: (params) =>
                Array.isArray(params)
                    ? renderTooltipHtml(params, { seriesByStreamId, getDisplayInfo, showDeviceName })
                    : "",
            axisPointer: {
                type: "cross",
                snap: false,
                label: {
                    color: "#FDFDFD",
                    backgroundColor: "#31332E",
                    padding: [0, 2],
                    fontSize: 12,
                    fontWeight: 400,
                    borderRadius: 0,
                    margin: -10,
                    align: "left",
                },
            },
        },
        xAxis: {
            type: "time",
            // The x-axis should show the user's desired time range - not whatever the actual set of
            // downloaded data starts and ends at.
            min: window.startMs,
            max: window.endMs,
            tooltip: {
                show: false,
            },
            axisLine: {
                show: false,
            },
            axisPointer: {
                label: {
                    margin: X_AXIS_POINTER_LABEL_MARGIN_PX,
                    formatter: (params) => formatAxisPointerTime(params.value as number | string | Date),
                },
            },
            axisLabel: {
                color: "#838879",
                fontSize: X_AXIS_TICK_LABEL_FONT_SIZE_PX,
                lineHeight: X_AXIS_TICK_LABEL_LINE_HEIGHT_PX,
                margin: X_AXIS_TICK_LABEL_MARGIN_PX,
                showMaxLabel: false,
                showMinLabel: false,
            },
            axisTick: {
                show: false,
            },
            splitLine: {
                show: true,
                showMinLine: false,
                showMaxLine: false,
                lineStyle: {
                    color: GRID_BORDER_COLOR,
                },
            },
        },
        yAxis: buildYAxes(spec.axes, axisScales, activeLeftAxisId),
        series: series.map((s) => {
            const entry = spec.streams.find((stream) => stream.streamId === s.streamId);
            const axis = spec.axes.find((a) => a.id === entry?.axisId);

            return {
                id: String(s.streamId),
                yAxisId: axis?.id,
                data: s.points,
                type: "line",
                showSymbol: false,
                color: s.color,
            };
        }),
    };
};
